import { HistoryResponse, ChatStreamRequest } from '../types';
import { AppError } from '../types/errors'; // Import the custom error type
import useAuthStore from '../store/authStore'; // Added import for authStore
import { fetchAndProcessSSE } from '../utils/sseUtils'; // Import the new utility
import * as Sentry from "@sentry/react"; // Added Sentry

// const API_BASE_URL = '/api/v1'; // Using Vite proxy - REMOVED This was causing issues in WP context

// Helper function to get the dynamic base URL
const getDynamicApiBaseUrl = () => {
  const state = useAuthStore.getState();
  // Ensure apiUrls and backendBaseUrl are available. 
  // authService provides a primary fallback if backend_api_url is not sent from WP,
  // so state.apiUrls.backendBaseUrl should ideally always be a string after init.
  // This provides an additional safeguard.
  return state.apiUrls?.backendBaseUrl || 'http://127.0.0.1:8000/api/v1'; 
};

/**
 * Fetches chat history for a given session ID.
 * @param sessionId The UUID of the chat session.
 * @param limit The maximum number of messages per page.
 * @param offset The number of messages to skip.
 * @returns A promise that resolves to the HistoryResponse object.
 */
export async function fetchChatHistory(
  sessionId: string,
  limit: number = 30,
  offset: number = 0
): Promise<HistoryResponse> {
  const API_BASE_URL = getDynamicApiBaseUrl(); // Get dynamic base URL
  const url = `${API_BASE_URL}/history?sessionId=${encodeURIComponent(
    sessionId
  )}&limit=${limit}&offset=${offset}`;

  const { jwt } = useAuthStore.getState(); // Get JWT from store
  const headers: HeadersInit = {
    Accept: 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      // Identify specific HTTP errors
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch history' }));
      const errorMessage = errorData.detail || `HTTP error ${response.status}`;

      Sentry.captureMessage(`Failed to fetch chat history: ${errorMessage}`, { // Added Sentry
        contexts: { fetch_error: { status: response.status, url: response.url, response_body: errorData } },
        level: "warning", 
      });

      if (response.status === 401) {
        console.log('Authentication Error (401) encountered in fetchChatHistory.');
        useAuthStore.getState().setAuthError(errorMessage);
      } else if (response.status === 403) {
        console.log('Authorization Error (403) encountered in fetchChatHistory.');
        useAuthStore.getState().setAuthError(errorMessage); // Treat 403 similar to 401 for store state
      } else if (response.status === 429) {
        console.log('Rate Limit Error (429) encountered in fetchChatHistory.');
        useAuthStore.getState().setRateLimitError(true, errorMessage);
      }
      console.error('Error fetching history:', response.status, errorData);
      // Throw a custom error object with status and message
      const err: AppError = new Error(errorMessage);
      err.status = response.status;
      err.detail = errorData.detail; // Store detail if available
      throw err;
    }

    return await response.json() as HistoryResponse;
  } catch (error) {
    console.error('Network error fetching history:', error);
    // Re-throw the error so the UI can handle it
    // If it's already our custom error, re-throw it, otherwise wrap it
    Sentry.captureException(error, { tags: { service: "chatService", method: "fetchChatHistory" } }); // Added Sentry
    if (error && (error as AppError).status !== undefined) {
      throw error;
    }
    const networkError: AppError = new Error(error instanceof Error ? error.message : 'An unknown network error occurred');
    networkError.status = 0; // Or a specific code for network errors
    throw networkError;
  }
}

/**
 * Define the new response type for session summaries (mirroring backend schema)
 * This might be better placed in a dedicated types file (e.g., types/chat.ts or types/index.ts)
 * For now, adding it here for context. If types/index.ts is preferred, we'll move it.
 */
export interface ChatSessionSummary {
  session_id: string; // UUID string
  title: string | null;
  last_activity_at: string; // ISO datetime string
  message_count: number;
}

export interface PaginatedChatSessionSummaryResponse {
  sessions: ChatSessionSummary[];
  total_sessions: number;
  limit: number;
  offset: number;
}

/**
 * Fetches paginated chat session summaries for the authenticated user.
 * @param limit The maximum number of session summaries per page.
 * @param offset The number of session summaries to skip.
 * @returns A promise that resolves to the PaginatedChatSessionSummaryResponse object.
 */
export async function fetchChatSessionSummaries(
  limit: number = 10,
  offset: number = 0
): Promise<PaginatedChatSessionSummaryResponse> {
  const API_BASE_URL = getDynamicApiBaseUrl(); // Get dynamic base URL
  const url = `${API_BASE_URL}/history/sessions?limit=${limit}&offset=${offset}`;

  const { jwt } = useAuthStore.getState(); // Get JWT from store
  const headers: HeadersInit = {
    Accept: 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch session summaries' }));
      const errorMessage = errorData.detail || `HTTP error ${response.status}`;

      Sentry.captureMessage(`Failed to fetch session summaries: ${errorMessage}`, { // Added Sentry
        contexts: { fetch_error: { status: response.status, url: response.url, response_body: errorData } },
        level: "warning",
      });

      if (response.status === 401) {
        useAuthStore.getState().setAuthError(errorMessage);
      } else if (response.status === 403) {
        useAuthStore.getState().setAuthError(errorMessage); 
      } else if (response.status === 429) {
        useAuthStore.getState().setRateLimitError(true, errorMessage);
      }
      console.error('Error fetching session summaries:', response.status, errorData);
      const err: AppError = new Error(errorMessage);
      err.status = response.status;
      err.detail = errorData.detail;
      throw err;
    }

    return await response.json() as PaginatedChatSessionSummaryResponse;
  } catch (error) {
    console.error('Network error fetching session summaries:', error);
    Sentry.captureException(error, { tags: { service: "chatService", method: "fetchChatSessionSummaries" } }); // Added Sentry
    if (error && (error as AppError).status !== undefined) {
      throw error;
    }
    const networkError: AppError = new Error(error instanceof Error ? error.message : 'An unknown network error occurred while fetching summaries');
    networkError.status = 0; 
    throw networkError;
  }
}

/**
 * Initiates a streaming chat response from the backend and processes SSE events.
 * @param requestData The data including sessionId, message, and optional currentPageUrl.
 * @param onToken Callback for when a 'token' event is received.
 * @param onEnd Callback for when the stream ends.
 * @param onError Callback for when an error occurs (from stream or initial request).
 * @param onStreamControllerCreated Callback to provide the AbortController to the store for cancellation.
 */
export async function streamChatResponse(
  requestData: ChatStreamRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (errorMessage: string | Error) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void
): Promise<void> {
  const API_BASE_URL = getDynamicApiBaseUrl(); // Get dynamic base URL
  const url = `${API_BASE_URL}/chat/stream`;
  const abortController = new AbortController();
  onStreamControllerCreated(abortController); // Pass the controller to the caller

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    reader = await fetchAndProcessSSE({
      url,
      method: 'POST',
      body: requestData,
      signal: abortController.signal,
      onOpen: () => {
        // Optional: can be logged or used to update UI state if needed
        console.log('SSE stream opened via chatService.');
      },
      onMessage: (data, eventType) => {
        // The fetchAndProcessSSE utility sends both data and eventType.
        // For chat, we primarily care about the 'token' data or unnamed data events.
        if (eventType === 'token' || !eventType) { // Handle 'token' or unnamed data events
          try {
            // UMW-Debug: Log raw data received by onMessage in chatService
            console.log(`[chatService] onMessage (token event): Received raw data: "${data}"`);
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.token === 'string') {
              // UMW-Debug: Log the successfully parsed token
              console.log(`[chatService] onMessage (token event): Parsed token: "${parsedData.token}"`);
              onToken(parsedData.token);
            } else {
              console.warn('[chatService] onMessage (token event): Parsed data is missing a valid token string. Data:', parsedData);
              // Optionally, call onError or handle as a non-token event if appropriate
            }
          } catch (e) {
            console.error('[chatService] onMessage (token event): Failed to parse JSON data or invalid structure. Error:', e, 'Raw data:', data);
            // Potentially call onError if this is considered a stream-breaking error
            // onError(new Error(`Failed to parse token event data: ${data.substring(0,100)}...`));
          }
        } else if (eventType === 'end') {
          // fetchAndProcessSSE calls its onEnd for this, which then calls our onEnd
          // console.log("[chatService] onMessage: Received 'end' event type.");
        } else if (eventType === 'error') {
          // fetchAndProcessSSE calls its onError for this, which then calls our onError
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null); // Clear controller when stream ends naturally
      },
      onError: (err) => {
        // The error from fetchAndProcessSSE can be Error object or string.
        // Ensure it's propagated correctly.
        onError(err instanceof Error ? err : new Error(String(err)));
        onStreamControllerCreated(null); // Clear controller on error
      },
    });
  } catch (error) {
    // This catch block is for errors thrown directly by fetchAndProcessSSE before it even starts processing,
    // or if fetchAndProcessSSE itself is not structured to call its own onError for all its failure modes.
    // However, fetchAndProcessSSE is designed to call its onError.
    console.error('Error in streamChatResponse calling fetchAndProcessSSE:', error);
    const errToReport = error instanceof Error ? error : new Error(String(error));
    onError(errToReport);
    onStreamControllerCreated(null); // Clear controller on error
    if (abortController.signal.aborted === false) {
        abortController.abort(); // Ensure abort if error happened outside of signal being aborted
    }
  } finally {
    // If fetchAndProcessSSE returned a reader and it wasn't cleaned up by an abort signal causing fetchAndProcessSSE to error early,
    // or if an error occurred after reader was assigned but before full cleanup in fetchAndProcessSSE's own error path (which it should have).
    // The main idea is that fetchAndProcessSSE should handle its reader if it successfully gets one.
    // This finally block is a safeguard.
    if (reader && !abortController.signal.aborted) {
        try {
            await reader.cancel('streamChatResponse cleanup');
            reader.releaseLock();
            console.log('streamChatResponse: Reader cancelled and released in finally block.');
        } catch (e) {
            console.error('streamChatResponse: Error in finally block cleaning up reader:', e);
        }
    }
    // Ensure the controller is cleared if it hasn't been already by onEnd/onError
    // This might be redundant if onEnd/onError always fire and clear it.
    // onStreamControllerCreated(null); 
  }
} 